import { Box } from "@chakra-ui/react";
import React, { RefObject, useRef } from "react";
import { useParams } from "react-router";
import { DeleteEnrollmentPeriodButton } from "src/components/Buttons/DeleteEnrollmentPeriodButton";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { AdminPage } from "src/components/Layout/AdminPage";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabsWithHash,
} from "src/components/Layout/TabsWithHash";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { NavigationBlockerProvider } from "src/components/NavigationBlocker";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { EnrollmentPeriodIdProvider, useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import { ENROLLMENT_PERIOD_TAB_HASH } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { CapacityTab } from "./CapacityTab";
import { EnrollmentSchoolsTab } from "./EnrollmentSchoolsTab/EnrollmentSchoolsTab";
import { FormTemplatesTab } from "./FormTemplatesTab/FormTemplatesTab";
import { GeneralTab } from "./GeneralTab/GeneralTab";
import { GET_ENROLLMENT_PERIOD_BY_ID } from "./graphql/queries";

export type EnrollmentPeriodContextType = {
  enrollmentPeriod: GQL.GetEnrollment_enrollment_period_by_pk;
  actionBarRef: RefObject<HTMLDivElement>;
  showFormButtons: boolean;
  setShowFormButtons: (show: boolean) => void;
};

export const EnrollmentPeriodContext =
  React.createContext<EnrollmentPeriodContextType | null>(null);

export const useEnrollmentPeriodContext = () => {
  const context = React.useContext(EnrollmentPeriodContext);
  if (!context) {
    throw new Error(
      "useEnrollmentPeriodContext must be used within EnrollmentPeriodContext.Provider"
    );
  }
  return context;
};

export const EditEnrollmentPeriod = (): React.ReactElement | null => {
  const organization = useOrganization();
  const { glossary } = useGlossary();
  const { enrollmentPeriodId = "" } = useParams();
  const [showFormButtons, setShowFormButtons] = React.useState(false);
  const actionBarRef = useRef<HTMLDivElement>(null);

  const { remoteData } = useRemoteDataQuery<
    GQL.GetEnrollment,
    GQL.GetEnrollmentVariables
  >(GET_ENROLLMENT_PERIOD_BY_ID, {
    variables: { id: enrollmentPeriodId },
    fetchPolicy: "no-cache",
  });

  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {(data) => {
        if (data.enrollment_period_by_pk === null) {
          return <NotFound />;
        }

        const { enrollment_period_by_pk: enrollmentPeriod } = data;

        const contextValue: EnrollmentPeriodContextType = {
          enrollmentPeriod,
          actionBarRef,
          showFormButtons,
          setShowFormButtons,
        };

        return (
          <EnrollmentPeriodIdProvider enrollmentPeriodId={enrollmentPeriodId}>
            <AdminPage
              breadcrumb={
                <Breadcrumb
                  items={breadcrumb.enrollmentPeriod.getBreadcrumbsForEdit(
                    organization,
                    enrollmentPeriod
                  )}
                />
              }
              heading={
                <AdminHeading
                  title={enrollmentPeriod.name}
                  id={enrollmentPeriodId}
                  actionButton={
                    <DeleteEnrollmentPeriodButton
                      id={enrollmentPeriodId}
                      enrollmentPeriodName={enrollmentPeriod.name}
                      isDisabled={
                        !!enrollmentPeriod.form_templates_aggregate.aggregate
                          ?.count
                      }
                    />
                  }
                />
              }
            >
              <EnrollmentPeriodContext.Provider value={contextValue}>
                <NavigationBlockerProvider>
                  <TabsWithHash variant="admin" isLazy>
                    <TabList>
                      <Tab
                        hashKey={ENROLLMENT_PERIOD_TAB_HASH.GENERAL}
                      >{glossary`General`}</Tab>
                      <Tab
                        hashKey={ENROLLMENT_PERIOD_TAB_HASH.CAPACITY}
                      >{glossary`Capacity`}</Tab>
                      <Tab
                        hashKey={ENROLLMENT_PERIOD_TAB_HASH.SCHOOLS}
                      >{glossary`Enrollment schools`}</Tab>
                      <Tab
                        hashKey={ENROLLMENT_PERIOD_TAB_HASH.FORMS}
                      >{glossary`Forms`}</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        <GeneralTab />
                      </TabPanel>
                      <TabPanel>
                        <CapacityTab enrollmentPeriodId={enrollmentPeriodId} />
                      </TabPanel>
                      <TabPanel>
                        <EnrollmentSchoolsTab />
                      </TabPanel>
                      <TabPanel>
                        <FormTemplatesTab />
                      </TabPanel>
                    </TabPanels>
                  </TabsWithHash>
                </NavigationBlockerProvider>
                {showFormButtons && (
                  <AdminFormButtons sticky>
                    <Box w="100%" ref={actionBarRef}></Box>
                  </AdminFormButtons>
                )}
              </EnrollmentPeriodContext.Provider>
            </AdminPage>
          </EnrollmentPeriodIdProvider>
        );
      }}
    </GQLRemoteDataView>
  );
};
